import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../hooks/useAuth";
import type { UserInfo } from "../types/api/auth";
import { useMixPanel } from "../hooks/useMixPanel";
import { T } from "../hooks/useTranslate";
import {
  UNICITY_GO_MARKETS,
  UNICITY_GO_URL,
  UNICITY_GO_IOS_URL,
  UNICITY_GO_ANDROID_URL,
  UNICITY_GO_SESSION_KEY,
} from "../common/unicityGoMarkets";

const getMobilePlatform = (): "ios" | "android" | "unknown" => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "unknown";
};

const getFeatureFlagFromQueryString = (flagName: string): string | false =>
  window?.location?.hash
    ?.split("?")[1]
    ?.split("&")
    ?.find((p) => p.indexOf(flagName) !== -1)
    ?.split("=")
    ?.slice(-1)[0] || false;

const UnicityGoIcon = () => (
  <img
    src="favicon.ico"
    alt="Unicity Go"
    width={36}
    height={36}
    style={{ borderRadius: 12, objectFit: "contain", objectPosition: "center" }}
  />
);

const UnicityGoBanner = () => {
  const { userInfo } = useAuth() as unknown as { userInfo: UserInfo };
  const { logEvent } = useMixPanel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(UNICITY_GO_SESSION_KEY) === "true";
  });
  const [hasLoggedShown, setHasLoggedShown] = useState(false);

  const isWebView = getFeatureFlagFromQueryString("isWebView");
  const isPwa = userInfo.isPwa;
  const isLoggedIn = userInfo.loggedIn;
  const countryCode = userInfo.countryCode;

  const isVisible =
    isLoggedIn &&
    !isPwa &&
    !isWebView &&
    isMobile &&
    !dismissed &&
    countryCode &&
    UNICITY_GO_MARKETS.has(countryCode);

  useEffect(() => {
    if (isVisible && !hasLoggedShown) {
      logEvent("unicity_go_banner_shown");
      setHasLoggedShown(true);
    }
  }, [isVisible, hasLoggedShown, logEvent]);

  if (!isVisible) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(UNICITY_GO_SESSION_KEY, "true");
    setDismissed(true);
    logEvent("unicity_go_banner_dismissed");
  };

  const platform = getMobilePlatform();

  const handleOpen = () => {
    logEvent("unicity_go_banner_opened", { platform });

    const url =
      platform === "ios"
        ? UNICITY_GO_IOS_URL
        : platform === "android"
          ? UNICITY_GO_ANDROID_URL
          : UNICITY_GO_URL;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      data-test-id="unicity-go-banner"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <IconButton
        data-test-id="unicity-go-banner-close"
        onClick={handleDismiss}
        size="small"
        aria-label="Dismiss banner"
        sx={{ color: "text.secondary", p: 0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <UnicityGoIcon />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}
        >
          <T>unicity_go</T>
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", lineHeight: 1.2 }}
        >
          <T>unicity_go_subtitle</T>
        </Typography>
      </Box>

      <Button
        data-test-id="unicity-go-banner-open"
        onClick={handleOpen}
        variant="outlined"
        size="small"
        sx={{
          borderRadius: 5,
          textTransform: "none",
          fontWeight: 600,
          borderColor: "#003764",
          color: "#003764",
          whiteSpace: "nowrap",
          "&:hover": {
            borderColor: "#002a4e",
            backgroundColor: "rgba(0, 55, 100, 0.04)",
          },
        }}
      >
        <T>unicity_go_open</T>
      </Button>
    </Box>
  );
};

export default UnicityGoBanner;
